import React, { useState, useRef } from 'react';
import clsx from 'clsx';

const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 20000000;
const KILO_BYTES_PER_BYTE = 1000;

const convertBytesToKB = (bytes: number) => Math.round(bytes / KILO_BYTES_PER_BYTE);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const convertNestedObjectToArray = (nestedObj: any) =>
    Object.keys(nestedObj).map((key) => nestedObj[key]);

function UploadIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M7 16a4 4 0 01-.88-7.9A5 5 0 1115.9 6h.6a4.5 4.5 0 110 9H16m-4-5v9m0-9l-3 3m3-3l3 3"
            />
        </svg>
    );
}

function DocumentIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
        </svg>
    );
}

function TrashIcon({ className }: { className?: string }) {
    return (
        <svg
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
        </svg>
    );
}

export default function FileUpload({
    label,
    updateFilesCb,
    maxFileSizeInBytes = DEFAULT_MAX_FILE_SIZE_IN_BYTES,
    onError,
    ...otherProps
}: {
    label?: string;
    maxFileSizeInBytes?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateFilesCb?: (files: File[]) => void;
    onError?: (error: string) => void;
    multiple: boolean;
}) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fileInputField: any = useRef(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [files, setFiles] = useState({} as any);
    const [isDragging, setIsDragging] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const addNewFiles = (newFiles: any) => {
        // eslint-disable-next-line no-restricted-syntax
        for (const file of newFiles) {
            if (file.size < maxFileSizeInBytes) {
                if (!otherProps.multiple) {
                    return { file };
                }
                files[file.name] = file;
            } else if (onError) {
                onError(
                    `The file exceeds the maximum upload size of ${maxFileSizeInBytes / 1000.0}kb`
                );
            }
        }
        return { ...files };
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callUpdateFilesCb = (filesToUpdate: any) => {
        const filesAsArray = convertNestedObjectToArray(filesToUpdate);
        if (updateFilesCb) {
            updateFilesCb(filesAsArray);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleNewFileUpload = (e: any) => {
        const { files: newFiles } = e.target;
        if (newFiles.length) {
            const updatedFiles = addNewFiles(newFiles);
            setFiles(updatedFiles);
            callUpdateFilesCb(updatedFiles);
        }
        setIsDragging(false);
    };

    const removeFile = (fileName: string) => {
        delete files[fileName];
        setFiles({ ...files });
        callUpdateFilesCb({ ...files });
    };

    const selected = Object.keys(files);

    return (
        <>
            {label && <div className="text-sm font-medium pb-1.5 text-label">{label}</div>}

            {/*
              The file input is stretched invisibly over the whole panel rather
              than hidden, which is what gives drag-and-drop for free - a drop
              anywhere on the panel lands on the input. The drag state is only
              for the highlight.
            */}
            <div
                onDragEnter={() => setIsDragging(true)}
                onDragLeave={() => setIsDragging(false)}
                onDrop={() => setIsDragging(false)}
                className={clsx(
                    'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors',
                    isDragging
                        ? 'border-orange-500 bg-accent-soft'
                        : 'border-border bg-surface-muted hover:border-border-strong'
                )}
            >
                <UploadIcon
                    className={clsx(
                        'w-8 h-8 mb-3',
                        isDragging ? 'text-orange-500' : 'text-foreground-muted'
                    )}
                />

                <div className="text-sm text-foreground">
                    <span className="font-semibold text-orange-500">Click to browse</span>
                    {' or drag and drop'}
                </div>
                <div className="text-xs text-foreground-muted mt-1">
                    {otherProps.multiple ? 'You can select more than one file. ' : ''}
                    Up to {Math.round(maxFileSizeInBytes / KILO_BYTES_PER_BYTE / 1000)}MB per file.
                </div>

                <input
                    type="file"
                    ref={fileInputField}
                    onChange={handleNewFileUpload}
                    onClick={() => {
                        // Clearing the value lets the same file be picked again
                        // after it has been removed from the list.
                        if (fileInputField.current) fileInputField.current.value = '';
                    }}
                    title=""
                    aria-label={label || 'Choose files to upload'}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    {...otherProps}
                />
            </div>

            {selected.length > 0 && (
                <div className="mt-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-foreground-secondary mb-2">
                        {selected.length === 1
                            ? '1 file selected'
                            : `${selected.length} files selected`}
                    </div>

                    <ul className="space-y-2">
                        {selected.map((fileName) => {
                            const file = files[fileName];
                            return (
                                <li
                                    key={fileName}
                                    className="flex items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2"
                                >
                                    <DocumentIcon className="w-5 h-5 flex-shrink-0 text-foreground-muted" />

                                    {/* min-w-0 so a long file name truncates instead of
                                        pushing the remove button out of the row. */}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-foreground truncate">
                                            {file.name}
                                        </div>
                                        <div className="text-xs text-foreground-muted">
                                            {convertBytesToKB(file.size)} kb
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => removeFile(fileName)}
                                        aria-label={`Remove ${file.name}`}
                                        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg text-foreground-muted transition-colors hover:text-danger hover:bg-surface-muted focus:outline-none"
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </>
    );
}
